import React from "react"
import { graphql } from "gatsby"
import {
  Link,
  useTranslation,
  Trans,
} from "@zestia/gatsby-plugin-react-i18next"

import useHeadTranslation from "@/hooks/use-head-translation"

import findImage from "@/utils/find-image"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import PrimaryHeader from "@/components/PrimaryHeader"
import SplitPane from "@/components/SplitPane"
import IntegrationsHighlight from "@/components/IntegrationsHighlight"
import Accordion from "@/components/Accordion"
import AccordionItem from "@/components/AccordionItem"
import TryFreeCallToAction from "@/components/TryFreeCallToAction"

import salesPipelineVideo from "@/videos/sales-pipeline.mp4"
import taskListVideo from "@/videos/task-list.mp4"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("freelancers.meta-title")}
      description={t("freelancers.meta-description")}
    />
  )
}

const FreelancersPage = ({ data }) => {
  const { t } = useTranslation()

  return (
    <MainLayout
      trackingData={{
        content_category: "sector",
        intent: "bofu",
      }}
    >
      <PrimaryHeader
        backgroundClass="bg-amber-300"
        title={t("freelancers.header.title")}
        description={t("freelancers.header.description")}
        image={{
          src: findImage(data.allFile, "hero-freelancers"),
          alt: t("freelancers.header.image-alt"),
        }}
      />

      <SplitPane
        assetAlignment="stacked"
        title={t("freelancers.affordable-plans.title")}
        text={t("freelancers.affordable-plans.firstParagraph")}
      />

      <SplitPane
        assetAlignment="right"
        video={salesPipelineVideo}
        title={t("freelancers.customizable-crm.title")}
      >
        <p className="mb-8 p--lg">
          {t("freelancers.customizable-crm.firstParagraph")}
        </p>
        <p className="mb-8 p--lg">
          {t("freelancers.customizable-crm.secondParagraph")}
        </p>
      </SplitPane>

      <SplitPane
        assetAlignment="stacked"
        title={t("freelancers.customer-support.title")}
        text={t("freelancers.customer-support.firstParagraph")}
      />

      <SplitPane
        assetAlignment="left"
        video={taskListVideo}
        title={t("freelancers.level-up.title")}
      >
        <p className="mb-8 p--lg">{t("freelancers.level-up.firstParagraph")}</p>
      </SplitPane>

      <SplitPane
        assetAlignment="stacked"
        title={t("freelancers.work-remotely.title")}
        text={t("freelancers.work-remotely.firstParagraph")}
      />

      <IntegrationsHighlight
        title={t("common.integration-highlight-title-default")}
        description={t("freelancers.integration-highlight.firstParagraph")}
        integrationData={data.integrations}
        integrations={["Better Proposals", "Zapier", "Xero", "Gmail"]}
        text="true"
        integrationsFooterLink="true"
      />

      <Accordion
        title={t("freelancers.why-a-crm.title")}
        description={t("freelancers.why-a-crm.firstParagraph")}
      >
        <AccordionItem title={t("freelancers.why-a-crm.itemOne.title")}>
          <p>{t("freelancers.why-a-crm.itemOne.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("freelancers.why-a-crm.itemTwo.title")}>
          <p>{t("freelancers.why-a-crm.itemTwo.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("freelancers.why-a-crm.itemThree.title")}>
          <p>{t("freelancers.why-a-crm.itemThree.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("freelancers.why-a-crm.itemFour.title")}>
          <p>{t("freelancers.why-a-crm.itemFour.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("freelancers.why-a-crm.itemFive.title")}>
          <p>{t("freelancers.why-a-crm.itemFive.firstParagraph")}</p>
          <p>{t("freelancers.why-a-crm.itemFive.secondParagraph")}</p>
        </AccordionItem>
      </Accordion>

      <Accordion
        title={t("freelancers.work-at-your-own-pace.title")}
        description={t("freelancers.work-at-your-own-pace.firstParagraph")}
      >
        <p className="mb-8 p--lg">
          {t("freelancers.work-at-your-own-pace.secondParagraph")}
        </p>
        <AccordionItem
          title={t("freelancers.work-at-your-own-pace.itemOne.title")}
        >
          <p>{t("freelancers.work-at-your-own-pace.itemOne.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem
          title={t("freelancers.work-at-your-own-pace.itemTwo.title")}
        >
          <p>{t("freelancers.work-at-your-own-pace.itemTwo.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem
          title={t("freelancers.work-at-your-own-pace.itemThree.title")}
        >
          <p>
            {t("freelancers.work-at-your-own-pace.itemThree.firstParagraph")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t("freelancers.work-at-your-own-pace.itemFour.title")}
        >
          <p>
            {t("freelancers.work-at-your-own-pace.itemFour.firstParagraph")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t("freelancers.work-at-your-own-pace.itemFive.title")}
        >
          <p>
            {t("freelancers.work-at-your-own-pace.itemFive.firstParagraph")}
          </p>
        </AccordionItem>
      </Accordion>

      <SplitPane
        assetAlignment="stacked"
        title={t("freelancers.free-crm.title")}
      >
        <p className="mb-8 p--lg">{t("freelancers.free-crm.firstParagraph")}</p>
        <p className="mb-8 p--lg">
          <Trans
            i18nKey="freelancers.free-crm.secondParagraph"
            components={{
              PaidPlans: (
                <Link className="text-link text-link--bare" to="/signup/" />
              ),
            }}
          />
        </p>
      </SplitPane>

      <TryFreeCallToAction
        title={t("freelancers.footer.title")}
        description={t("freelancers.footer.description")}
      />

      <Accordion className="mt-20" title={t("common.faq")}>
        <AccordionItem title={t("freelancers.faqs.one.question")}>
          <p>{t("freelancers.faqs.one.answer")}</p>
        </AccordionItem>

        <AccordionItem title={t("freelancers.faqs.two.question")}>
          <p>{t("freelancers.faqs.two.answer")}</p>
        </AccordionItem>

        <AccordionItem title={t("freelancers.faqs.three.question")}>
          <p>{t("freelancers.faqs.three.answer")}</p>
        </AccordionItem>

        <AccordionItem title={t("freelancers.faqs.four.question")}>
          <p>{t("freelancers.faqs.four.answer")}</p>
        </AccordionItem>

        <AccordionItem title={t("freelancers.faqs.five.question")}>
          <p>{t("freelancers.faqs.five.answer")}</p>
        </AccordionItem>

        <AccordionItem title={t("freelancers.faqs.six.question")}>
          <p>{t("freelancers.faqs.six.answer")}</p>
        </AccordionItem>

        <AccordionItem title={t("freelancers.faqs.seven.question")}>
          <p>{t("freelancers.faqs.seven.answer")}</p>
        </AccordionItem>

        <AccordionItem title={t("freelancers.faqs.eight.question")}>
          <p>{t("freelancers.faqs.eight.answer")}</p>
        </AccordionItem>

        <AccordionItem title={t("freelancers.faqs.ten.question")}>
          <p>{t("freelancers.faqs.ten.answer")}</p>
        </AccordionItem>
      </Accordion>
    </MainLayout>
  )
}

export const query = graphql`
  query ($language: String) {
    ...Locales
    ...Integrations
    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "industries/freelancers" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`

export default FreelancersPage
